import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { customerService } from '../../services/customerService';
import api, { apiWithTimeout } from '../../services/api';
import toast from 'react-hot-toast';

/**
 * Custom hook encapsulating all data loading, mutations,
 * and UI state for the PurchaseInvoiceDetailsPage.
 */
export default function usePurchaseInvoice(id) {
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { hasPermission } = usePermissions();
  const canGeneratePZ = hasPermission('faktury_zakupu', 'generuj_pz', selectedLocation?.id);

  // Core data
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection
  const [selectedItems, setSelectedItems] = useState([]);

  // Mapping modal
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingItem, setMappingItem] = useState(null);
  const [autoMappingLoading, setAutoMappingLoading] = useState(false);

  // Edit item modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Product search (mapping modal)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Supplier search
  const [showSupplierSearch, setShowSupplierSearch] = useState(false);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [supplierSearchResults, setSupplierSearchResults] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const supplierSearchRef = useRef(null);
  const supplierDropdownRef = useRef(null);

  // Correction modal
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);

  // ===== Data loading =====

  useEffect(() => {
    if (id) loadInvoiceDetails();
  }, [id]);

  const loadInvoiceDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/purchase-invoices/${id}`);
      const data = response.data;
      if (data.success) {
        setInvoice(data.data);
      } else {
        setError(data.message || 'Blad ladowania faktury');
      }
    } catch (err) {
      console.error('Blad ladowania faktury:', err);
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  // ===== Supplier search & update =====

  const handleSupplierSearch = async (query) => {
    setSupplierSearchQuery(query);
    if (query.length < 2) {
      setSupplierSearchResults([]);
      return;
    }
    try {
      setSupplierSearchLoading(true);
      // Rola 'dostawca' - kartoteka jest wspolna (klient + dostawca w pos_klienci),
      // wiec bez filtra roli kasjer dostawalby tu klientow detalicznych.
      const results = await customerService.searchCustomers(query, 15, 'dostawca');
      const customers = results.data?.customers || results.customers || results.data || results || [];
      setSupplierSearchResults(Array.isArray(customers) ? customers : []);
    } catch (err) {
      console.error('Blad wyszukiwania dostawcow:', err);
      setSupplierSearchResults([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  const selectSupplier = async (supplier) => {
    try {
      const supplierName = supplier.typ_klienta === 'firma'
        ? (supplier.nazwa_firmy || supplier.name)
        : (supplier.name || `${supplier.imie || ''} ${supplier.nazwisko || ''}`.trim());

      // GLOWNA PRZYCZYNA wolnego tekstu na fakturach: ten ekran wysylal
      // WYLACZNIE snapshot tekstowy, wiec faktura nigdy nie dostawala ID karty.
      // customer_id backend mapuje na kolumne faktury_zakupowe.kontrahent_id
      // (stara dostawca_id zostaje nietknieta - ma inne znaczenie historyczne).
      // Pola tekstowe NADAL wysylamy: to snapshot dokumentu, nie join.
      const supplierData = {
        customer_id: supplier.id || null,
        nazwa_dostawcy: supplierName,
        nip_dostawcy: supplier.nip || '',
        adres_dostawcy: supplier.address || supplier.adres || '',
        telefon_dostawcy: supplier.phone || supplier.telefon || '',
        email_dostawcy: supplier.email || ''
      };

      const response = await api.put(`/purchase-invoices/${id}`, supplierData);
      const result = response.data;

      if (result.success) {
        await loadInvoiceDetails();
        setShowSupplierSearch(false);
        setSupplierSearchQuery('');
        setSupplierSearchResults([]);
      } else {
        toast.error('Blad aktualizacji dostawcy: ' + (result.message || 'Nieznany blad'));
      }
    } catch (err) {
      console.error('Blad aktualizacji dostawcy:', err);
      toast.error('Blad polaczenia z serwerem');
    }
  };

  const handleNewSupplierAdded = async (newCustomer) => {
    setShowAddSupplierModal(false);
    if (newCustomer) {
      await selectSupplier(newCustomer);
    }
  };

  // Close supplier dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        supplierSearchRef.current &&
        !supplierSearchRef.current.contains(event.target) &&
        supplierDropdownRef.current &&
        !supplierDropdownRef.current.contains(event.target)
      ) {
        setShowSupplierSearch(false);
        setSupplierSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ===== Auto-mapping =====

  const autoMapInvoiceItems = async () => {
    try {
      setAutoMappingLoading(true);
      setError(null);
      const response = await api.post(`/purchase-invoices/${id}/auto-map`);
      const data = response.data;
      if (data.success) {
        await loadInvoiceDetails();
        toast.success(`Mapowanie zakonczone! EAN: ${data.data.mapped_by_ean}, kod: ${data.data.mapped_by_code}, nazwy: ${data.data.updated_names}, razem: ${data.data.total_processed}`);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Blad podczas automatycznego mapowania');
      console.error('Blad auto-mapowania:', err);
    } finally {
      setAutoMappingLoading(false);
    }
  };

  // ===== Warehouse receipt generation =====

  const generateWarehouseReceipt = async () => {
    // H-loc5: bez wybranej lokalizacji NIE wolno generowac PZ.
    // Zaden sztywny fallback (wczesniej: `|| 5`) — wygenerowalby PZ na zly magazyn.
    if (!selectedLocation?.id) {
      toast.error('Wybierz lokalizację');
      return;
    }
    try {
      setAutoMappingLoading(true);
      setError(null);
      const warehouseId = selectedLocation.id;
      // PZ generation moze trwac dluzej przy duzych fakturach (100+ pozycji)
      // - per-request timeout 3 min (uzywamy glownego api zeby zachowac CSRF/device interceptors).
      // Backend jest idempotentny: przy retry zwraca istniejace PZ.
      const response = await api.post(`/warehouse/external-receipt/${id}`, {
        warehouse_id: warehouseId,
        location_id: warehouseId,
      }, { timeout: 180000 });
      const data = response.data;
      if (data.success) {
        // Numer PZ pokazujemy WYLACZNIE taki, jaki nadal backend. Wczesniej przy
        // braku document_number front sklejal wlasny 'PZ/{id}/{miesiac}/{rok}',
        // przez co operator widzial w komunikacie numer inny niz ten na dokumencie.
        const docNum = data.data.document_number;
        const alreadyExisted = data.data.already_existed;
        if (alreadyExisted) {
          toast.info(docNum
            ? `PZ ${docNum} już istnieje (zostało wygenerowane wcześniej)`
            : 'PZ dla tej faktury już istnieje (zostało wygenerowane wcześniej)');
        } else {
          const lokalizacja = selectedLocation?.name || 'domyślna';
          toast.success(docNum
            ? `PZ wygenerowane: ${docNum}, lokalizacja: ${lokalizacja}`
            : `PZ wygenerowane, lokalizacja: ${lokalizacja}`);
        }
        await loadInvoiceDetails();
      } else {
        toast.error(`Blad: ${data.error}`);
      }
    } catch (err) {
      // M-timeout: interceptor w api.js usuwa err.code i rzuca nowy Error(message),
      // wiec detekcja po err.code jest martwa. Timeout rozpoznajemy po fladze isTimeout
      // (ustawianej przez interceptor) oraz po czytelnym komunikacie timeoutu.
      // Przy timeoucie backend moze wciaz konczyc PZ w tle (jest idempotentny),
      // wiec NIE pokazujemy bledu i NIE zachecamy do ponawiania (to grozi duplikatem PZ).
      const isTimeout = err?.isTimeout === true || /timeout|czas oczekiwania/i.test(err?.message || '');
      if (isTimeout) {
        toast('Generowanie PZ trwa w tle — odśwież za chwilę i sprawdź historię PZ (nie ponawiaj).', { icon: '⏳' });
      } else {
        toast.error('Blad podczas generowania PZ');
      }
      console.error('Blad generowania PZ:', err);
      // Odswiez dane — moze PZ sie zapisal mimo timeoutu UI
      try { await loadInvoiceDetails(); } catch (_) {}
    } finally {
      setAutoMappingLoading(false);
    }
  };

  // ===== Selection =====

  const handleItemSelection = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(i => i !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAllItems = () => {
    if (!invoice?.items || invoice.items.length === 0) return;
    if (selectedItems.length === invoice.items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(invoice.items.map(item => item.id));
    }
  };

  // ===== Sorting =====

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedItems = () => {
    if (!invoice?.items) return [];
    if (!sortConfig.key) return invoice.items;
    return [...invoice.items].sort((a, b) => {
      let aVal = a[sortConfig.key] || '';
      let bVal = b[sortConfig.key] || '';
      if (sortConfig.key === 'nazwa_produktu') {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // ===== Mapping modal =====

  const openMappingModal = (item) => {
    setMappingItem(item);
    setShowMappingModal(true);
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      setSearchLoading(true);
      const response = await api.get(`/products/search?query=${encodeURIComponent(query)}&limit=20`);
      const data = response.data;
      if (data.success) {
        setSearchResults(data.data?.products || []);
      }
    } catch (err) {
      console.error('Blad wyszukiwania:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchProducts(query);
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSearchQuery(product.name || product.nazwa);
    setSearchResults([]);
  };

  const handleSaveMapping = async () => {
    if (!mappingItem || !selectedProduct) {
      toast.error('Wybierz produkt do zmapowania');
      return;
    }
    try {
      const response = await api.post(`/purchase-invoices/${id}/items/${mappingItem.id}/map`, {
        product_id: selectedProduct.id
      });
      const data = response.data;
      if (data.success) {
        setShowMappingModal(false);
        setMappingItem(null);
        setSelectedProduct(null);
        setSearchQuery('');
        await loadInvoiceDetails();
        toast.success('Produkt zmapowany pomyslnie!');
      } else {
        toast.error('Blad: ' + data.message);
      }
    } catch (err) {
      console.error('Blad mapowania:', err);
      toast.error('Blad mapowania produktu');
    }
  };

  // ===== Edit item modal =====

  // M-edit-po-pz: po wygenerowaniu PZ status faktury = 'dodana' (patrz
  // warehouse_operations_service.generate_external_receipt). Edycja ilosci/ceny
  // pozycji po PZ rozjezdza fakture ze stanami/FIFO — blokujemy w UI, backend
  // dodatkowo to wymusza (409). Zmiana tylko przez korekte.
  const isPzGenerated = invoice?.status === 'dodana' || invoice?.has_pz === true || !!invoice?.pz_count;

  const openEditModal = (item) => {
    if (isPzGenerated) {
      toast.error('Faktura ma wygenerowane PZ — edycja pozycji tylko przez korektę.');
      return;
    }
    setEditingItem(item);
    const ilosc = parseFloat(item.ilosc) || 1;
    const cenaNetto = parseFloat(item.cena_netto) || 0;
    const stawkaVat = parseFloat(item.stawka_vat) || 23;
    const cenaBrutto = parseFloat((cenaNetto * (1 + stawkaVat / 100)).toFixed(2));
    const wartoscNetto = parseFloat((ilosc * cenaNetto).toFixed(2));
    const wartoscBrutto = parseFloat((ilosc * cenaBrutto).toFixed(2));

    setEditFormData({
      ilosc,
      cena_netto: cenaNetto,
      cena_brutto: cenaBrutto,
      wartosc_netto: wartoscNetto,
      wartosc_brutto: wartoscBrutto,
      stawka_vat: stawkaVat
    });
    setShowEditModal(true);
  };

  const recalculateEditPrices = (field, value, currentData) => {
    const data = { ...currentData };
    data[field] = parseFloat(value) || 0;
    const ilosc = data.ilosc || 0;
    const stawkaVat = data.stawka_vat || 0;

    if (field === 'cena_netto' || field === 'stawka_vat' || field === 'ilosc') {
      const cenaNetto = data.cena_netto || 0;
      data.cena_brutto = parseFloat((cenaNetto * (1 + stawkaVat / 100)).toFixed(2));
      data.wartosc_netto = parseFloat((ilosc * cenaNetto).toFixed(2));
      data.wartosc_brutto = parseFloat((ilosc * data.cena_brutto).toFixed(2));
    } else if (field === 'cena_brutto') {
      const cenaBrutto = data.cena_brutto || 0;
      data.cena_netto = parseFloat((cenaBrutto / (1 + stawkaVat / 100)).toFixed(2));
      data.wartosc_netto = parseFloat((ilosc * data.cena_netto).toFixed(2));
      data.wartosc_brutto = parseFloat((ilosc * cenaBrutto).toFixed(2));
    }
    return data;
  };

  const handleEditFieldChange = (field, value) => {
    const newData = recalculateEditPrices(field, value, editFormData);
    setEditFormData(newData);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    try {
      const response = await api.put(`/purchase-invoices/${id}/items/${editingItem.id}`, editFormData);
      const data = response.data;
      if (data.success) {
        setShowEditModal(false);
        setEditingItem(null);
        await loadInvoiceDetails();
        toast.success('Pozycja zaktualizowana pomyslnie!');
      } else {
        toast.error('Blad: ' + data.message);
      }
    } catch (err) {
      console.error('Blad zapisu:', err);
      toast.error('Blad zapisu zmian');
    }
  };

  // ===== Quick name update =====

  const handleQuickNameUpdate = async (productId, newName) => {
    if (!productId || !newName) return;
    if (!window.confirm(`Czy na pewno chcesz zmienić nazwę produktu w bazie na:\n\n"${newName}"?`)) {
      return;
    }
    try {
      const response = await api.put(`/products/${productId}/update-name`, { nazwa: newName });
      const data = response.data;
      if (data.success) {
        toast.success('Nazwa produktu zaktualizowana!');
        await loadInvoiceDetails();
      } else {
        toast.error('Blad: ' + (data.error || data.message));
      }
    } catch (err) {
      console.error('Blad aktualizacji nazwy:', err);
      toast.error('Blad aktualizacji nazwy produktu');
    }
  };

  return {
    // Core data
    invoice,
    loading,
    error,
    navigate,
    selectedLocation,
    canGeneratePZ,
    loadInvoiceDetails,

    // Selection
    selectedItems,
    handleItemSelection,
    handleSelectAllItems,

    // Sorting
    sortConfig,
    handleSort,
    getSortedItems,

    // Auto-mapping & PZ
    autoMappingLoading,
    autoMapInvoiceItems,
    generateWarehouseReceipt,

    // Supplier search
    showSupplierSearch,
    setShowSupplierSearch,
    supplierSearchQuery,
    supplierSearchResults,
    supplierSearchLoading,
    handleSupplierSearch,
    selectSupplier,
    supplierSearchRef,
    supplierDropdownRef,
    showAddSupplierModal,
    setShowAddSupplierModal,
    handleNewSupplierAdded,

    // Edit item modal
    showEditModal,
    setShowEditModal,
    editingItem,
    editFormData,
    isPzGenerated,
    openEditModal,
    handleEditFieldChange,
    handleSaveEdit,

    // Mapping modal
    showMappingModal,
    setShowMappingModal,
    mappingItem,
    openMappingModal,
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    selectedProduct,
    setSelectedProduct,
    handleSearchChange,
    handleProductSelect,
    handleSaveMapping,

    // Quick name update
    handleQuickNameUpdate,

    // Correction modal
    showCorrectionModal,
    setShowCorrectionModal
  };
}
